import { Link } from 'react-router-dom';
import { motion } from 'framer-motion'; // Motion ekledik
import ScholarLinks from '../components/ScholarLinks';
import cvData from '../data/cvData';
import statistics from '../data/statistics';

// Grid elemanları için animasyon varyantları
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { type: "spring", stiffness: 100 } }
};

export default function Home() {
  const quickStats = [
    { label: 'Yayın', value: statistics.publications.total, icon: '📚', link: '/papers' },
    { label: 'Proje', value: statistics.projects.total, icon: '🔬', link: '/projects' },
    { label: 'Öğrenci', value: statistics.students.masters + statistics.students.phd, icon: '👥', link: '/students' },
    { label: 'Deneyim', value: `${statistics.yearsOfExperience}+`, icon: '🎓', link: '/cv' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Profile Section */}
        <div className="flex flex-col md:flex-row items-center md:items-start gap-12 mb-16">
          <motion.div 
            initial={{ opacity: 0, scale: 0.8, rotate: -2 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 0.8, type: "spring" }}
            className="flex-shrink-0"
          >
            <div className="w-56 h-56 rounded-2xl border-4 border-gray-200 shadow-xl overflow-hidden bg-white p-2 hover:border-navy transition-colors duration-300">
              <img 
                src="https://unis.ahievran.edu.tr/app_files/2025/09/Kisi_Logo_936_0b715b14.webp" 
                alt={cvData.personal.name}
                className="w-full h-full rounded-xl object-cover"
              />
            </div>
          </motion.div>
          
          <div className="flex-1 text-center md:text-left">
            <motion.h1 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="text-5xl md:text-6xl font-serif font-bold text-navy mb-4 tracking-tight"
            >
              {cvData.personal.name}
            </motion.h1>
            
            <motion.div 
              initial={{ width: 0 }}
              animate={{ width: "6rem" }} // w-24 = 6rem
              transition={{ delay: 0.4, duration: 0.8 }}
              className="h-1 bg-gradient-to-r from-transparent via-navy to-transparent mx-auto md:mx-0 mb-4"
            ></motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
                <p className="text-2xl text-gray-700 mb-2 font-medium">{cvData.personal.title}</p>
                <p className="text-lg text-gray-600 mb-6">{cvData.personal.university}</p>
                <p className="text-gray-700 leading-relaxed max-w-2xl text-lg">
                  Elektrik-Elektronik Mühendisliği alanında RF parmak izi, IoT cihaz kimlik tespiti, 
                  makine öğrenmesi ve elektronik burun (electronic nose) uygulamaları konularında 
                  araştırmalar yürüten deneyimli bir akademisyenim. Arduino tabanlı sensör sistemleri, 
                  gıda kalite tespiti ve kablosuz iletişim sistemleri üzerine çalışmalar yapmaktayım.
                </p>
            </motion.div>
          </div>
        </div>

        {/* Quick Statistics */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16"
        >
          {quickStats.map((stat, index) => {
            const colors = [
              { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-600' },
              { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-600' },
              { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-600' },
              { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-600' }
            ];
            const color = colors[index % colors.length];
            
            return (
              <motion.div variants={itemVariants} key={index}>
                <Link to={stat.link}>
                  <div className={`bg-white rounded-xl border-2 ${color.border} p-6 text-center hover:shadow-xl transition-all duration-300 group cursor-pointer h-full`}>
                    <div className={`${color.bg} w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4 text-3xl group-hover:scale-110 transition-transform duration-300`}>
                      {stat.icon}
                    </div>
                    <div className={`text-4xl font-bold ${color.text} mb-2`}>{stat.value}</div>
                    <div className="text-sm font-medium text-gray-600 uppercase tracking-wide">{stat.label}</div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Research Interests */}
        <div className="mb-16">
          <motion.h2 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-serif font-bold text-navy mb-8 text-center md:text-left"
          >
            Araştırma İlgi Alanları
          </motion.h2>
          
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="flex flex-wrap gap-3 justify-center md:justify-start"
          >
            {cvData.researchInterests.map((interest, index) => (
              <motion.span
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.05, y: -2 }}
                className="px-5 py-2.5 bg-white rounded-full text-navy border-2 border-gray-300 hover:border-navy hover:bg-navy hover:text-white transition-colors duration-300 cursor-default font-medium text-sm shadow-sm"
              >
                {interest}
              </motion.span>
            ))}
          </motion.div>
        </div>

        {/* Scholar Links */}
        <div>
          <motion.h2 
             initial={{ opacity: 0, x: -20 }}
             whileInView={{ opacity: 1, x: 0 }}
             viewport={{ once: true }}
             className="text-4xl font-serif font-bold text-navy mb-8 text-center md:text-left"
          >
            Akademik Profiller
          </motion.h2>
          <ScholarLinks />
        </div>
      </div>
    </div>
  );
}